/**
 * Centralized helpers to ensure that sensitive flows (checkout, login, infos-festa)
 * always run on the canonical app origin, even when the user starts the journey on a
 * white-label store domain (e.g., amecardapio.com, *.ameup.com.br supplier slug,
 * or a custom domain configured by the supplier).
 *
 * The white-label hosts are intentionally restricted to the public store/share
 * surface. The full SPA (auth, checkout, dashboards) lives at the canonical origin.
 */

const CANONICAL_APP_ORIGIN = "https://ameup.com.br";

// Hosts that are considered "first-class" SPA hosts where every route is allowed.
// Anything else is treated as white-label and must be redirected for sensitive paths.
const FIRST_CLASS_APP_HOSTS = [
  "ameup.com.br",
  "www.ameup.com.br",
  "ameup2026.lovable.app",
];

const isLovablePreviewHost = (hostname: string): boolean =>
  hostname.endsWith(".lovable.app") ||
  hostname.endsWith(".lovable.dev") ||
  hostname.endsWith(".lovableproject.com") ||
  hostname === "localhost" ||
  hostname === "127.0.0.1";

/**
 * Returns the canonical origin for the main application.
 * On preview/lovable/localhost we keep the current origin so dev flows keep working.
 */
export const getCanonicalAppOrigin = (): string => {
  if (typeof window === "undefined") return CANONICAL_APP_ORIGIN;
  const hostname = window.location.hostname;
  if (isLovablePreviewHost(hostname)) {
    return window.location.origin;
  }
  return CANONICAL_APP_ORIGIN;
};

/**
 * Whether the current host is a white-label / store host where sensitive
 * SPA flows (checkout, login, infos-festa) must be redirected to the canonical origin.
 */
export const isWhiteLabelHost = (): boolean => {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;

  // Dev / preview is never white-label — full SPA available locally.
  if (isLovablePreviewHost(hostname)) return false;

  // Known canonical hosts.
  if (FIRST_CLASS_APP_HOSTS.includes(hostname)) return false;

  // Anything else (amecardapio.com, supplier-slug.ameup.com.br, supplier custom domain)
  // is white-label.
  return true;
};

/**
 * Build an absolute URL on the canonical app origin for the given path+query.
 * Accepts a path like "/checkout/abc?foo=bar" and returns the full URL.
 */
export const buildCanonicalAppUrl = (pathAndQuery: string): string => {
  const origin = getCanonicalAppOrigin();
  const normalized = pathAndQuery.startsWith("/")
    ? pathAndQuery
    : `/${pathAndQuery}`;
  return `${origin}${normalized}`;
};

/**
 * Navigate to a sensitive path. If we're on a white-label host, force a hard
 * redirect to the canonical origin. Otherwise, fall back to the SPA router.
 */
export const navigateToCanonical = (
  pathAndQuery: string,
  spaNavigate: (to: string) => void,
): void => {
  if (isWhiteLabelHost()) {
    window.location.href = buildCanonicalAppUrl(pathAndQuery);
    return;
  }
  spaNavigate(pathAndQuery);
};
